/* eslint-disable no-nested-ternary */
/* eslint-disable react/destructuring-assignment */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import DateFormatter from 'Common/Components/DateFormatter';
import get from 'lodash/get';
import {
  Expander, Icon, Hostnames, SeverityColumn, CenteredText, CenteredNumericRow,
  CenteredIcon, CenteredHeader
} from './styled';
import {
  CheckColum, CheckHeader, IpColumn
} from './components';
import ImportanceColumn from './components/ImportanceColumn';
import { TagCell } from './components/Tag';
import formatNumber from 'Common/Functions/FormatNumber';
import {
  getCreatorToolData, getCreatorCommandIdData, getMacData, getParametersData, joinUniqueValues
} from './columnsData';

const oss = ['windows', 'cisco', 'router', 'osx', 'apple', 'mac', 'linux', 'unix'];
function getHost (h) {
  return h ? oss.find((os) => (h.toLowerCase().indexOf(os) > -1 ? os : '')) : '';
}

const getDataOrPlaceholder = (value) => (value && value.length > 0 ? value : '');

const getNumericDataOrPlaceholder = (number) => (number && number > 0 ? number : '');

const getVulnsCountValue = (number) => (typeof number === 'number' ? number : 0);

const getHostnameString = (hostnames, groupBy, isPivot) => {
  if (hostnames.length === 0 || (groupBy && !isPivot)) { return ''; }
  const hostnameArray = hostnames.split(',');
  return hostnameArray.length > 1 ? `${hostnameArray[0]} + ${hostnameArray.length - 1}` : hostnameArray[0];
};

const getAssetName = (host = {}) => host.name || get(host, 'hostnames[0]', '') || host.ip || '';

const parseServiceSummary = (summary = '') => {
  const match = summary.match(/^\((\d+)\/([^)]+)\)\s+(.+)$/);
  if (match) return { port: match[1], protocol: match[2], name: match[3] };
  return { port: null, protocol: null, name: summary };
};

const getDetailedServices = (host = {}) => {
  if (Array.isArray(host.services_status) && host.services_status.length > 0) return host.services_status;
  if (Array.isArray(host.services)) return host.services;
  return [];
};

const getServiceSummaryData = (host = {}, field) =>
  (host.service_summaries || []).map((summary) => parseServiceSummary(summary)[field]).filter(Boolean);

const getServicesNamesData = (host = {}) => joinUniqueValues(
  getDetailedServices(host).map((service) => service?.name),
  getServiceSummaryData(host, 'name')
);

const getServicesPortsData = (host = {}) => joinUniqueValues(
  host.service_ports,
  getDetailedServices(host).map((service) => service?.ports ?? service?.port),
  getServiceSummaryData(host, 'port')
);

const getServicesStatusesData = (host = {}) => joinUniqueValues(
  host.service_statuses,
  getDetailedServices(host).map((service) => service?.status)
);

/**
 * Assets table of a workspace (main table and the group-by sub-table, via `isPivot`).
 * ctx: `{ isPivot, fields, groupBy, isFetching, isAllowedByLicense, theme }`
 * (`theme` is `themeStyles`).
 */
const hostSchema = {
  id: 'host',
  loading: { mockRows: 13, skeleton: ({ isFetching }) => isFetching },
  ordering: {},
  columns: [{
    header: (headerInfo, { groupBy, isPivot }) => groupBy && !isPivot && <></>,
    id: 'expander',
    visible: ({ groupBy, isPivot }) => !!groupBy && !isPivot,
    sortable: false,
    filterable: false,
    minWidth: 50,
    order: -1,
    expander: ({ isExpanded, original }, { groupBy, isFetching }) => {
      const groupingBy = get(original, groupBy, '-');
      return (
        <Expander isExpanded={ isExpanded }>
          {!isFetching && `${groupingBy} (${original.count})`}
        </Expander>
      );
    }
  }, {
    header: (headerInfo, { groupBy, isPivot }) => (!groupBy || (groupBy && isPivot)) && <CheckHeader />,
    id: 'selected',
    accessor: 'Selected',
    visible: ({ groupBy, isPivot }) => (!groupBy || (groupBy && isPivot)),
    sortable: false,
    resizable: false,
    maxWidth: 38,
    minWidth: 38,
    style: { display: 'flex' },
    cell: (rowInfo, { groupBy, isPivot }) => (!groupBy || isPivot) && <CheckColum host={ rowInfo.original } />
  },
  {
    header: () => (
      <CenteredHeader>
        <FormattedMessage id="host.table.columns.important" />
      </CenteredHeader>
    ),
    id: 'importance',
    accessor: 'importance',
    visible: ({ fields, groupBy, isPivot }) => (fields.importance?.visible || !groupBy || (groupBy && isPivot)),
    sortable: ({ groupBy }) => !groupBy,
    resizable: false,
    width: 90,
    // minWidth: 50,
    order: ({ fields }) => fields.importance?.order,
    cell: (rowInfo, { groupBy, isPivot }) => (!groupBy || isPivot) && <ImportanceColumn host={ rowInfo.original } />

  },
  {
    header: () => <FormattedMessage id="host.table.columns.name" />,
    id: 'ip',
    accessor: 'ip',
    visible: ({ fields, groupBy }) => fields.name?.visible && groupBy !== 'ip',
    sortable: ({ groupBy }) => !groupBy,
    resizable: ({ groupBy }) => !groupBy,
    minWidth: ({ fields }) => fields.name?.minWidth,
    maxWidth: ({ fields }) => fields.name?.maxWidth,
    order: ({ fields }) => fields.name?.order,
    cell: (rowInfo, { groupBy, isPivot }) => <IpColumn host={ rowInfo.original } showIcon={ !groupBy || (groupBy && isPivot) }>{getDataOrPlaceholder(getAssetName(rowInfo.original), groupBy)}</IpColumn>,
    cellProps: (rowInfo, { fields }) => ({ className: `name min-wd-${fields.name?.defaultMin}` })
  }, {
    header: () => <FormattedMessage id="host.table.columns.risk" />,
    id: 'risk',
    accessor: 'risk',
    visible: ({ fields, isAllowedByLicense, groupBy }) => fields.risk?.visible && isAllowedByLicense && !groupBy,
    sortable: ({ groupBy }) => !groupBy,
    resizable: ({ groupBy }) => !groupBy,
    maxWidth: ({ fields, groupBy }) => (groupBy ? fields.risk?.defaultMin : fields.risk?.maxWidth),
    minWidth: ({ fields }) => fields.risk?.minWidth,
    cellProps: (rowInfo, { fields }) => ({ className: `min-wd-${fields.risk?.defaultMin}` }),
    order: ({ fields }) => fields.risk?.order,
    cell: (rowInfo, { groupBy }) => <CenteredNumericRow>{ getNumericDataOrPlaceholder(rowInfo.original.risk, groupBy) } </CenteredNumericRow>
  }, {
    header: () => <CenteredHeader><FormattedMessage id="host.table.columns.search" /></CenteredHeader>,
    id: 'search',
    accessor: 'search',
    visible: ({ fields }) => fields.search?.visible,
    sortable: false,
    resizable: false,
    order: ({ fields }) => fields.search?.order,
    cell: (rowInfo, { groupBy }) => (
      !groupBy && rowInfo.original.ip
        ? (
          <a href={ `//shodan.io/search?query=${rowInfo.original.ip}` } target="_blank" rel="noopener noreferrer" title="Search in Shodan">
            <Icon alt="Search in Shodan" type="shodan" />
          </a>
          )
        : null
    ),
    maxWidth: ({ fields }) => fields.search?.maxWidth
  }, {
    header: () => (
      <CenteredHeader>
        <FormattedMessage id="host.table.columns.os" />
      </CenteredHeader>
    ),
    id: 'os',
    accessor: 'os',
    visible: ({ fields, groupBy }) => fields.os?.visible && groupBy !== 'os',
    sortable: ({ groupBy }) => !groupBy,
    resizable: true,
    minWidth: ({ fields }) => fields.os?.minWidth,
    maxWidth: ({ fields }) => fields.os?.maxWidth,
    order: ({ fields }) => fields.os?.order,
    cell: (rowInfo, { groupBy, isPivot }) => {
      const os = getHost(rowInfo.original.os);
      return (!groupBy || (groupBy && isPivot)) && (
        <CenteredIcon>
          <Icon
            alt={ `${os}` }
            type={ os }
          />
        </CenteredIcon>
      );
    },
    cellProps: (rowInfo, { fields }) => ({ className: `os min-wd-${fields.os?.defaultMin}` })
  }, {
    header: () => <FormattedMessage id="host.table.columns.services" />,
    id: 'services__name',
    visible: ({ fields }) => fields.services?.visible,
    sortable: ({ groupBy }) => !groupBy,
    resizable: ({ groupBy }) => !groupBy,
    order: ({ fields }) => fields.services?.order,
    cell: (rowInfo) => getDataOrPlaceholder(getServicesNamesData(rowInfo.original)),
    maxWidth: 150,
    minWidth: ({ fields }) => fields.services?.minWidth,
    cellProps: (rowInfo, { fields, theme }) => ({ 'column-type': 'services', 'className': `min-wd-${fields.services?.defaultMin}`, 'style': { color: theme?.text?.tableVulnName } })
  }, {
    header: () => <FormattedMessage id="host.table.columns.hostnames" />,
    id: 'hostnames',
    visible: ({ fields }) => fields.hostnames?.visible,
    sortable: false,
    resizable: ({ groupBy }) => !groupBy,
    order: ({ fields }) => fields.hostnames?.order,
    cell: (rowInfo, { groupBy, isPivot }) => (rowInfo.original.hostnames
      ? <Hostnames>{ getDataOrPlaceholder(getHostnameString(rowInfo.original.hostnames.join(', '), groupBy, isPivot)) }</Hostnames>
      : ''),
    minWidth: 200,
    maxWidth: 300,
    cellProps: (rowInfo, { fields }) => ({ 'column-type': 'hostnames', 'className': `min-wd-${fields.hostnames?.defaultMin}` })
  }, {
    header: () => (
      <CenteredHeader>
        <FormattedMessage id="host.table.columns.vulns" />
      </CenteredHeader>
    ),
    id: 'vulnerability_count',
    accessor: 'vulns',
    visible: ({ fields }) => fields.vulns?.visible,
    sortable: ({ groupBy }) => !groupBy,
    resizable: ({ groupBy }) => !groupBy,
    order: ({ fields }) => fields.vulns?.order,
    maxWidth: ({ fields }) => fields.vulns?.maxWidth,
    minWidth: ({ fields }) => fields.vulns?.minWidth,
    cell: (rowInfo) => <CenteredNumericRow title={ getVulnsCountValue(rowInfo.original.vulns) }>{ formatNumber(getVulnsCountValue(rowInfo.original.vulns)) }</CenteredNumericRow>,
    cellProps: (rowInfo, { fields }) => ({ className: `min-wd-${fields.vulns?.defaultMin}` })
  }, {
    header: () => <CenteredHeader><FormattedMessage id="host.table.columns.owned" /></CenteredHeader>,
    id: 'owned',
    accessor: 'owned',
    visible: ({ fields }) => fields.owned?.visible,
    sortable: ({ groupBy }) => !groupBy,
    resizable: ({ groupBy }) => !groupBy,
    order: ({ fields }) => fields.owned?.order,
    cell: (rowInfo) => (
      <CenteredText>
        <FormattedMessage id={ rowInfo.original.owned ? 'host.table.columns.owned.true' : 'host.table.columns.owned.false' } />
      </CenteredText>
    ),
    maxWidth: 80,
    cellProps: (rowInfo, { fields, theme }) => ({ 'column-type': 'owned', 'className': `min-wd-${fields.owned?.defaultMin}`, 'style': { color: theme?.text?.tableVulnName } })
  }, {
    header: () => <FormattedMessage id="host.table.columns.description" />,
    id: 'description',
    accessor: 'description',
    visible: ({ fields }) => fields.description?.visible,
    sortable: ({ groupBy }) => !groupBy,
    resizable: ({ groupBy }) => !groupBy,
    order: ({ fields }) => fields.description?.order,
    cell: (rowInfo, { groupBy }) => getDataOrPlaceholder(rowInfo.original.description, groupBy),
    minWidth: 165,
    maxWidth: 210,
    cellProps: (rowInfo, { fields, theme }) => ({ 'column-type': 'description', 'className': `min-wd-${fields.description?.defaultMin}`, 'style': { color: theme?.text?.tableVulnName } })
  }, {
    header: () => <FormattedMessage id="host.table.columns.creator_tool" />,
    id: 'creator_command_tool',
    visible: ({ fields }) => fields.creator_command_tool?.visible,
    sortable: false,
    resizable: ({ groupBy }) => !groupBy,
    order: ({ fields }) => fields.creator_command_tool?.order,
    cell: (rowInfo) => getDataOrPlaceholder(getCreatorToolData(rowInfo.original)),
    minWidth: ({ fields }) => fields.creator_command_tool?.minWidth,
    cellProps: (rowInfo, { fields, theme }) => ({ className: `min-wd-${fields.creator_command_tool?.defaultMin}`, style: { color: theme?.text?.tableVulnName } })
  }, {
    header: () => <FormattedMessage id="host.table.columns.creator_command_id" />,
    id: 'creator_command_id',
    visible: ({ fields }) => fields.creator_command_id?.visible,
    sortable: false,
    resizable: ({ groupBy }) => !groupBy,
    order: ({ fields }) => fields.creator_command_id?.order,
    cell: (rowInfo) => getDataOrPlaceholder(getCreatorCommandIdData(rowInfo.original)),
    minWidth: ({ fields }) => fields.creator_command_id?.minWidth,
    cellProps: (rowInfo, { fields, theme }) => ({ className: `min-wd-${fields.creator_command_id?.defaultMin}`, style: { color: theme?.text?.tableVulnName } })
  }, {
    header: () => <FormattedMessage id="host.table.columns.mac" />,
    id: 'mac',
    accessor: 'mac',
    visible: ({ fields }) => fields.mac?.visible,
    sortable: ({ groupBy }) => !groupBy,
    resizable: ({ groupBy }) => !groupBy,
    order: ({ fields }) => fields.mac?.order,
    cell: (rowInfo, { groupBy }) => getDataOrPlaceholder(getMacData(rowInfo.original), groupBy),
    minWidth: 120,
    maxWidth: 150,
    cellProps: (rowInfo, { fields, theme }) => ({ 'column-type': 'mac', 'className': `min-wd-${fields.mac?.defaultMin}`, 'style': { color: theme?.text?.tableVulnName } })
  }, {
    header: () => (
      <CenteredHeader>
        <FormattedMessage id="host.table.columns.owner" />
      </CenteredHeader>
    ),
    id: 'creator__username',
    accesor: 'owner',
    visible: ({ fields, groupBy }) => fields.owner?.visible && groupBy !== 'owner',
    sortable: ({ groupBy }) => !groupBy,
    resizable: ({ groupBy }) => !groupBy,
    order: ({ fields }) => fields.owner?.order,
    cell: (rowInfo, { groupBy }) => (
      <CenteredText>
        {getDataOrPlaceholder(rowInfo.original.owner, groupBy)}
      </CenteredText>
    ),
    minWidth: ({ fields }) => fields.owner?.minWidth,
    maxWidth: ({ fields }) => fields.owner?.maxWidth,
    cellProps: (rowInfo, { fields }) => ({ className: `min-wd-${fields.owner?.defaultMin}` })
  }, {
    header: () => <FormattedMessage id="host.table.columns.parameters" />,
    id: 'parameters',
    visible: ({ fields }) => fields.parameters?.visible,
    sortable: false,
    resizable: ({ groupBy }) => !groupBy,
    order: ({ fields }) => fields.parameters?.order,
    cell: (rowInfo) => getDataOrPlaceholder(getParametersData(rowInfo.original)),
    minWidth: ({ fields }) => fields.parameters?.minWidth,
    cellProps: (rowInfo, { fields, theme }) => ({ className: `min-wd-${fields.parameters?.defaultMin}`, style: { color: theme?.text?.tableVulnName } })
  }, {
    header: () => <FormattedMessage id="host.table.columns.creation_time" />,
    id: 'create_date',
    visible: ({ fields }) => fields.creation_time?.visible,
    sortable: ({ groupBy }) => !groupBy,
    resizable: ({ groupBy }) => !groupBy,
    order: ({ fields }) => fields.creation_time?.order,
    cell: (rowInfo) => (
      rowInfo.original.metadata && rowInfo.original.metadata.create_time ? <DateFormatter date={ rowInfo.original.metadata.create_time } /> : ''
    ),
    minWidth: ({ fields }) => fields.creation_time?.minWidth,
    maxWidth: ({ fields }) => fields.creation_time?.maxWidth,
    cellProps: (rowInfo, { fields, theme }) => ({ className: `min-wd-${fields.creation_time?.defaultMin}`, style: { color: theme?.text?.tableVulnName } })
  }, {
    header: () => (
      <CenteredHeader>
        <FormattedMessage id="host.table.columns.last_modified" />
      </CenteredHeader>
    ),
    id: 'update_date',
    accesor: 'update_date',
    visible: ({ fields }) => fields.last_modified?.visible,
    sortable: ({ groupBy }) => !groupBy,
    resizable: ({ groupBy }) => !groupBy,
    order: ({ fields }) => fields.last_modified?.order,
    cell: (rowInfo) => (
      <CenteredIcon>
        {rowInfo.original.metadata && rowInfo.original.metadata.update_time ? <DateFormatter date={ rowInfo.original.metadata.update_time } /> : ''}
      </CenteredIcon>
    ),
    minWidth: ({ fields }) => fields.last_modified?.minWidth,
    maxWidth: ({ fields }) => fields.last_modified?.maxWidth,
    width: ({ fields }) => fields.last_modified?.defaultMin,
    cellProps: (rowInfo, { fields, theme }) => ({ className: `last_modified min-wd-${fields.last_modified?.defaultMin}`, style: { color: theme?.text?.tableVulnName } })
  }, {
    header: () => <FormattedMessage id="host.table.columns.severity_counts" />,
    id: 'severity_counts',
    visible: ({ fields }) => fields.severity_counts?.visible,
    sortable: ({ groupBy }) => !groupBy,
    resizable: ({ groupBy }) => !groupBy,
    order: ({ fields }) => fields.severity_counts?.order,
    cell: (rowInfo) => (rowInfo.original.severity_counts && <SeverityColumn value={ rowInfo.original.severity_counts } />) || '',
    minWidth: ({ fields }) => fields.severity_counts?.minWidth,
    maxWidth: ({ fields }) => fields.severity_counts?.maxWidth,
    cellProps: (rowInfo, { fields, theme }) => ({ 'column-type': 'severity_counts', 'className': `min-wd-${fields.severity_counts?.defaultMin}`, 'style': { color: theme.text.tableVulnName } })
  }, {
    header: () => (
      <CenteredHeader>
        <FormattedMessage id="host.table.columns.id" />
      </CenteredHeader>
    ),
    id: 'id',
    accesor: '_id',
    visible: ({ fields }) => fields.id?.visible,
    sortable: ({ groupBy }) => !groupBy,
    resizable: ({ groupBy }) => !groupBy,
    order: ({ fields }) => fields.id?.order,
    cell: (rowInfo) => (
      <CenteredText>
        {getNumericDataOrPlaceholder(rowInfo.original.id)}
      </CenteredText>
    ),
    width: 80,
    cellProps: (rowInfo, { fields }) => ({ 'column-type': 'id', 'className': `min-wd-${fields.id?.defaultMin}` })
  }, {
    header: () => <FormattedMessage id="host.table.columns.services_port" />,
    id: 'services_port',
    visible: ({ fields }) => fields.services_port?.visible,
    sortable: false,
    resizable: ({ groupBy }) => !groupBy,
    order: ({ fields }) => fields.services_port?.order,
    cell: (rowInfo) => getDataOrPlaceholder(getServicesPortsData(rowInfo.original)),
    minWidth: ({ fields }) => fields.services_port?.minWidth,
    cellProps: (rowInfo, { fields, theme }) => ({ className: `min-wd-${fields.services_port?.defaultMin}`, style: { color: theme?.text?.tableVulnName } })
  }, {
    header: () => <FormattedMessage id="host.table.columns.services_status" />,
    id: 'services_status',
    visible: ({ fields }) => fields.services_status?.visible,
    sortable: false,
    resizable: ({ groupBy }) => !groupBy,
    order: ({ fields }) => fields.services_status?.order,
    cell: (rowInfo) => getDataOrPlaceholder(getServicesStatusesData(rowInfo.original)),
    minWidth: ({ fields }) => fields.services_status?.minWidth,
    cellProps: (rowInfo, { fields, theme }) => ({ className: `min-wd-${fields.services_status?.defaultMin}`, style: { color: theme?.text?.tableVulnName } })
  }, {
    header: () => <FormattedMessage id="host.table.columns.status" />,
    id: 'status',
    accessor: 'status',
    visible: ({ fields }) => fields.status?.visible,
    sortable: false,
    resizable: ({ groupBy }) => !groupBy,
    order: ({ fields }) => fields.status?.order,
    cell: (rowInfo) => getDataOrPlaceholder(joinUniqueValues(rowInfo.original.status)),
    minWidth: ({ fields }) => fields.status?.minWidth,
    cellProps: (rowInfo, { fields, theme }) => ({ className: `min-wd-${fields.status?.defaultMin}`, style: { color: theme?.text?.tableVulnName } })
  }, {
    header: () => <div><FormattedMessage id="host.table.columns.tags" /></div>,
    accessor: 'tags',
    sortable: false,
    minWidth: ({ fields }) => fields.tags?.minWidth,
    maxWidth: ({ fields }) => fields.tags?.maxWidth,
    resizable: ({ groupBy }) => !groupBy,
    id: 'tags',
    visible: ({ fields, isTagsAllowedByLicense }) => fields.tags?.visible && isTagsAllowedByLicense,
    cell: (rowInfo, { isFetching }) => <TagCell rowInfo={ rowInfo } showSkeleton={ isFetching } />,
    cellProps: (rowInfo, { fields }) => ({ 'column-type': 'tags', 'className': `min-wd-${fields.tags?.defaultMin}` }),
    order: ({ fields }) => fields.tags?.order
  }]
};

export default hostSchema;
